import React from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { Layout } from './components/Layout';
import { useCustomer } from './hooks/useCustomer';
import { Onboarding } from './pages/Onboarding';
import { Dashboard } from './pages/Dashboard';
import { GettingStarted } from './pages/GettingStarted';
import { Licenses } from './pages/Licenses';
import { Account } from './pages/Account';
import { CssEditor } from './pages/CssEditor';
import { Upgrade } from './pages/Upgrade';

function PortalApp({ email }: { email: string }) {
  const { customer, licenses, loading, error, needsSignup, signup, updateProfile, updateCss, reload } = useCustomer();

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#6b7280' }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#dc2626' }}>
        {error}
      </div>
    );
  }

  if (needsSignup || !customer) {
    return <Onboarding email={email} onComplete={signup} />;
  }

  const handleSignOut = async () => {
    await signOut();
    window.location.reload();
  };

  return (
    <BrowserRouter>
      <Layout customerName={customer.name} onSignOut={handleSignOut}>
        <Routes>
          <Route path="/" element={<Dashboard customer={customer} licenses={licenses} onReload={reload} />} />
          <Route path="/getting-started" element={<GettingStarted licenses={licenses} />} />
          <Route path="/licenses" element={<Licenses licenses={licenses} />} />
          <Route path="/account" element={<Account customer={customer} onUpdate={updateProfile} />} />
          <Route path="/css" element={<CssEditor customer={customer} onSave={updateCss} />} />
          <Route path="/upgrade" element={<Upgrade licenses={licenses} />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

const authComponents = {
  Header() {
    return (
      <div className="auth-header">
        <div className="auth-logo">
          <svg width="40" height="40" viewBox="0 0 32 32">
            <rect width="32" height="32" rx="6" fill="#4f46e5" />
            <text x="16" y="23" fontFamily="system-ui, sans-serif" fontSize="18" fontWeight="700" fill="white" textAnchor="middle">CK</text>
          </svg>
        </div>
        <h1 className="auth-title">ChikChak Labs</h1>
        <p className="auth-subtitle">Manage your plugins, API keys, and account</p>
      </div>
    );
  },
  Footer() {
    return (
      <div className="auth-footer">
        <p>
          <a href="https://chikchaklabs.com" target="_blank" rel="noopener noreferrer">chikchaklabs.com</a>
          {' · '}
          <a href="https://chikchaklabs.com/docs" target="_blank" rel="noopener noreferrer">Docs</a>
          {' · '}
          <a href="mailto:support@chikchaklabs.com">Support</a>
        </p>
      </div>
    );
  },
};

function AuthWrapper() {
  const [authenticated, setAuthenticated] = React.useState(false);

  return (
    <div className={authenticated ? undefined : 'auth-page'}>
      <Authenticator components={authComponents}>
        {({ user }) => {
          if (!authenticated) setAuthenticated(true);
          return <PortalApp email={(user?.signInDetails?.loginId) ?? ''} />;
        }}
      </Authenticator>
    </div>
  );
}

export default function App() {
  return <AuthWrapper />;
}
